// frontend/src/pages/dashboard/ProjectComponents/ProjectCommentSection.js
import React, { useState, useEffect } from 'react';
import { MentionsInput, Mention } from "react-mentions";
import { toast } from "react-toastify";
import { Box, Typography, Button, Avatar, Chip, IconButton } from "@mui/material";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import AuthenticatedImage from './AuthenticatedImage';
import ImagePreview from './ImagePreview';
import { getAvatarUrl } from '../../../utils/avatarUtils';
import { highlightMentions } from "../../../utils/sanitizeContent";

const formatTimeAgo = (dateStr) => {
  const now = new Date();
  const commentDate = new Date(dateStr);
  const diffSec = Math.floor((now - commentDate) / 1000);
  if (diffSec < 60) return "Just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
};

const handlePasteImage = (event, projectId, setCommentFiles, toast) => {
  const items = event.clipboardData?.items;
  if (!items) return;
  const imageFiles = [];
  for (let i = 0; i < items.length; i++) {
    if (items[i].type.indexOf("image") !== -1) {
      const file = items[i].getAsFile();
      if (file) imageFiles.push(file);
    }
  }
  if (imageFiles.length > 0) {
    event.preventDefault();
    setCommentFiles((prev) => {
      const existingFiles = prev[projectId] || [];
      const existingArray = existingFiles instanceof FileList ? Array.from(existingFiles) : existingFiles;
      return { ...prev, [projectId]: [...existingArray, ...imageFiles] };
    });
    toast?.success(`📸 ${imageFiles.length} image(s) pasted`);
  }
};

const ProjectCommentSection = React.memo(({
  projectId,
  comments,
  loading,
  onPostComment,
  onDeleteComment,
  users,
  authUser,
  newCommentText,
  setNewCommentText,
  commentFiles,
  setCommentFiles,
  theme,
  hasPermission,
  handleDownload,
  handleImagePreview,
}) => {
  const [localText, setLocalText] = useState(newCommentText[projectId] || "");

  useEffect(() => {
    setLocalText(newCommentText[projectId] || "");
  }, [newCommentText, projectId]);

  const handleTextChange = (e, newValue) => {
    if (typeof newValue === "string") {
      setLocalText(newValue);
      setNewCommentText((prev) => ({ ...prev, [projectId]: newValue }));
    }
  };

  const handleFileChange = (e) => {
    setCommentFiles((prev) => ({ ...prev, [projectId]: e.target.files }));
  };

  const removeFile = (index) => {
    setCommentFiles((prev) => {
      const existing = prev[projectId] || [];
      const filtered = Array.from(existing).filter((_, i) => i !== index);
      return { ...prev, [projectId]: filtered };
    });
  };

  return (
    <Box size={{xs:12}} sx={{ p: 2.5, bgcolor: "background.paper", borderRadius: 2, mt: 1.5, mb: 2, border: "1px solid", borderColor: "divider", boxShadow: "0 2px 6px rgba(0,0,0,0.05)" }}>
      {/* Comment Input */}
      <Box sx={{ mb: 2.5 }}>
        <MentionsInput
          key={`mention-${projectId}`}
          value={localText}
          onChange={handleTextChange}
          onPaste={(e) => handlePasteImage(e, projectId, setCommentFiles, toast)}
          placeholder="Write a comment... Type @ to mention team members"
          markup="@[__display__](__id__)"
          allowSpaceInQuery
          style={{
            control: {
              backgroundColor: "#fff",
              fontSize: 15,
              minHeight: 90,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 12,
              padding: "12px",
              boxSizing: "border-box",
              width: "100%",
            },
            highlighter: { display: "none" },
            input: {
              padding: "12px",
              outline: "none",
              border: "none",
              width: "100%",
              backgroundColor: "transparent",
              color: "inherit",
              fontFamily: "inherit",
              fontSize: "inherit",
            },
            suggestions: {
              list: {
                backgroundColor: "white",
                border: "1px solid rgba(0,0,0,0.15)",
                fontSize: 14,
                maxHeight: 180,
                overflowY: "auto",
                borderRadius: 8,
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                zIndex: 9999,
              },
              item: {
                padding: "8px 12px",
                borderBottom: "1px solid rgba(0,0,0,0.1)",
                "&focused": { backgroundColor: "#e6f7ff" },
                cursor: "pointer",
              },
            },
          }}
        >
          <Mention
            trigger="@"
            markup="@[__display__](__id__)"
            displayTransform={(id, display) => `@${display}`}
            data={Array.isArray(users) ? users.filter((u) => u && u.name).map((u) => ({ id: String(u.id), display: u.name.trim() })) : []}
            appendSpaceOnAdd
            style={{ backgroundColor: "transparent", color: theme.palette.primary.main, fontWeight: 600, padding: "2px 4px", borderRadius: 4, border: "1px solid #e0e0e0" }}
          />
        </MentionsInput>

        {/* Attached Image Preview */}
        {commentFiles[projectId] && Array.from(commentFiles[projectId]).length > 0 && (
          <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
            {Array.from(commentFiles[projectId]).map((file, index) => (
              <ImagePreview key={index} file={file} index={index} onRemove={removeFile} />
            ))}
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.5 }}>
          <Button component="label" variant="outlined" startIcon={<AttachFileIcon />} size="small" sx={{ textTransform: "none" }}>
            Attach Files
            <input type="file" multiple hidden onChange={handleFileChange} />
          </Button>
          {commentFiles[projectId] && (
            <Typography variant="caption" color="text.secondary">
              {commentFiles[projectId].length} file(s) selected
            </Typography>
          )}
          <Button variant="contained" size="small" onClick={() => onPostComment(projectId)} disabled={!localText?.trim()} sx={{ textTransform: "none", fontWeight: 600, minWidth: 70 }}>
            Post
          </Button>
        </Box>
      </Box>

      {/* Comments List */}
      <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
        {loading ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>Loading comments...</Typography>
        ) : comments?.length > 0 ? (
          comments.map((comment) => (
            <Box key={comment.id} sx={{ p: 2, mb: 1.5, borderRadius: 2, bgcolor: "grey.50", borderLeft: `3px solid ${theme.palette.primary.main}`, "&:hover": { bgcolor: "action.hover" } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <Box sx={{ flexGrow: 1, mr: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <Avatar sx={{ width: 24, height: 24 }} src={getAvatarUrl(comment.user?.avatar)} onError={(e) => { e.target.onerror = null; e.target.src = null; }}>
                      {!comment.user?.avatar ? comment.user?.name?.charAt(0) : null}
                    </Avatar>
                    <Typography variant="subtitle2" fontWeight={600}>{comment.user?.name}</Typography>
                    {comment.user_id === authUser?.id && <Chip label="You" size="small" color="primary" />}
                  </Box>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6, "& span": { color: theme.palette.primary.main, fontWeight: 600 } }} dangerouslySetInnerHTML={{ __html: highlightMentions(comment.content) }} />
                  
                  {/* Comment Attachments */}
                  {comment.attachments?.length > 0 && (
                    <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {comment.attachments.map((attachment) =>
                        attachment.original_name?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                          <Box key={attachment.id} sx={{ position: "relative", width: 100, height: 100, borderRadius: 2, overflow: "hidden", border: "1px solid #e0e0e0", cursor: "pointer", "&:hover": { opacity: 0.9 } }} onClick={() => handleImagePreview(attachment)}>
                            <AuthenticatedImage attachment={attachment} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </Box>
                        ) : (
                          <Box key={attachment.id} sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, bgcolor: "background.paper", borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
                            <AttachFileIcon fontSize="small" color="action" />
                            <Typography variant="body2" noWrap sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{attachment.original_name}</Typography>
                            <IconButton size="small" onClick={() => handleDownload(attachment)}><DeleteIcon fontSize="small" /></IconButton>
                          </Box>
                        )
                      )}
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", ml: 1 }}>
                  <Typography variant="caption" color="textSecondary">{formatTimeAgo(comment.created_at)}</Typography>
                  {hasPermission("project.comment.delete") && (
                    <IconButton size="small" onClick={() => onDeleteComment(projectId, comment.id)} sx={{ mt: 0.5, color: "error.main" }}><DeleteIcon fontSize="small" /></IconButton>
                  )}
                </Box>
              </Box>
            </Box>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>No comments yet. Be the first to comment!</Typography>
        )}
      </Box>
    </Box>
  );
});

export default ProjectCommentSection;